<template>
  <div class="mosca-counter">
    <div class="mosca-buttons">
      <button type="button" class="mosca-btn mosca-btn--no" @click="increment('no')">
        {{ t('moscaCounter.stingNo') }}
      </button>
      <button type="button" class="mosca-btn mosca-btn--si" @click="increment('si')">
        {{ t('moscaCounter.stingYes') }}
      </button>
    </div>

    <div class="mosca-counts">
      <div class="mosca-count-box">
        <label>NO</label>
        <input type="number" min="0" :value="numNo" @change="onInputChange('no', $event)" class="mosca-input" />
      </div>
      <div class="mosca-count-box">
        <label>SI</label>
        <input type="number" min="0" :value="numSi" @change="onInputChange('si', $event)" class="mosca-input" />
      </div>
    </div>

    <div class="mosca-result">
      <div class="mosca-percentage" :class="percentageClass">
        {{ t('moscaCounter.infestationAt') }} <strong>{{ percentage }}</strong>%
      </div>
      <div class="mosca-summary" v-if="total > 0">
        {{ t('moscaCounter.stingsOutOfObserved', { si: numSi, total }) }}
      </div>
      <div class="mosca-motivation" v-if="motivation">
        {{ motivation }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ num_si: 0, num_no: 0 })
  }
})
const emit = defineEmits(['update:modelValue'])

const numSi = computed(() => Number(props.modelValue?.num_si) || 0)
const numNo = computed(() => Number(props.modelValue?.num_no) || 0)
const total  = computed(() => numSi.value + numNo.value)

const percentage = computed(() => {
  if (total.value === 0) return '-'
  return Math.round((numSi.value / total.value) * 100)
})

const percentageClass = computed(() => {
  if (total.value === 0) return ''
  const p = percentage.value
  if (p < 10)  return 'mosca-pct--low'
  if (p < 30)  return 'mosca-pct--medium'
  return 'mosca-pct--high'
})

const motivation = computed(() => {
  const n = total.value
  if (n === 0)          return t('moscaCounter.motivationFirst')
  if (n < 50)           return t('moscaCounter.motivationAtLeast50')
  if (n < 70)           return t('moscaCounter.motivationKeepGoing')
  if (n < 100)          return t('moscaCounter.motivationAlmost100')
  return t('moscaCounter.motivationGreatJob')
})

function increment(type) {
  emit('update:modelValue', {
    num_si: numSi.value + (type === 'si' ? 1 : 0),
    num_no: numNo.value + (type === 'no' ? 1 : 0),
  })
}

function onInputChange(type, event) {
  const val = Math.max(0, parseInt(event.target.value) || 0)
  emit('update:modelValue', {
    num_si: type === 'si' ? val : numSi.value,
    num_no: type === 'no' ? val : numNo.value,
  })
}
</script>

<style scoped>
.mosca-counter {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mosca-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.mosca-btn {
  padding: 20px 12px;
  font-size: 1.1rem;
  font-weight: 700;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  color: #fff;
  letter-spacing: 0.02em;
}
.mosca-btn:active {
  transform: scale(0.96);
  opacity: 0.85;
}
.mosca-btn--no  { background: #2e7d32; }
.mosca-btn--si  { background: #c62828; }

.mosca-counts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.mosca-count-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.mosca-count-box label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--ae-text-secondary, #666);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.mosca-input {
  width: 100%;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  border: 1px solid var(--ae-border, #ddd);
  border-radius: 6px;
  padding: 8px 4px;
  background: var(--ae-surface, #fff);
  color: var(--ae-text, #222);
}

.mosca-result {
  text-align: center;
  padding: 12px;
  background: var(--ae-surface-raised, #f5f5f5);
  border-radius: 8px;
}
.mosca-percentage {
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 4px;
}
.mosca-pct--low    { color: #2e7d32; }
.mosca-pct--medium { color: #f57f17; }
.mosca-pct--high   { color: #c62828; }

.mosca-summary {
  font-size: 0.9rem;
  color: var(--ae-text-secondary, #666);
  margin-bottom: 4px;
}
.mosca-motivation {
  font-size: 0.85rem;
  font-style: italic;
  color: var(--ae-text-secondary, #888);
}
</style>
